import { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaUser, FaBuilding, FaPhone, FaBullseye, FaChartLine, FaMoneyBillWave, FaExclamationCircle, FaRupeeSign, FaClock, FaCheckCircle, FaArrowRight, FaArrowLeft, FaPlus, FaCheck } from 'react-icons/fa';

function RequestFormModal({ isOpen, onClose }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    nameDesignation: '',
    companyPlace: '',
    mobileNumber: '',
    alternateMobile: '',
    expectations: [],
    turnover: '',
    amountStuck: '',
    painPoint: '',
    investment: '',
    callTiming: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Bundle states
  const [availableBundles, setAvailableBundles] = useState([]);
  const [selectedBundles, setSelectedBundles] = useState([]);
  const [loadingBundles, setLoadingBundles] = useState(false);

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_PAYMENT_API_URL || 'http://localhost:5005/api';

  const totalSteps = 5;

  const expectationOptions = [
    { value: 'credit-control', label: 'Credit Control System', icon: FaChartLine },
    { value: 'money-recovery', label: 'Money Recovery', icon: FaMoneyBillWave },
    { value: 'team-training', label: 'Training of your Team on Payment Recovery', icon: FaUser },
    { value: 'all', label: 'All of the Above', icon: FaCheckCircle }
  ];

  const amountStuckOptions = [
    'None',
    'Less Than 5 Lakhs',
    '5 -15 Lakhs',
    '15 - 30 Lakhs',
    '30 Lakhs - 50 Lakhs',
    '50 Lakhs - 1 Crore',
    'More than 1 Crore'
  ];

  const investmentOptions = [
    { value: '2.5', label: '₹2.5 Lacs' },
    { value: '3.75', label: '₹3.75 Lacs' },
    { value: '5', label: '₹5 Lacs' }
  ];

  const callTimingOptions = [
    { value: 'next-5-days', label: 'Within Next 5-Days', icon: FaClock },
    { value: '5-15-days', label: 'Within 5 - 15 Days', icon: FaClock },
    { value: 'no-hurry', label: 'No Hurry / Not Urgent', icon: FaClock }
  ];

  // Fetch available bundles from database
  const fetchBundles = useCallback(async () => {
    try {
      setLoadingBundles(true);
      const response = await fetch(`${API_BASE_URL}/payments/bundles/available?page=credit-control`);
      const data = await response.json();
      if (data.success) {
        // Filter out the current product from bundles
        const filteredBundles = data.bundles.filter(b => 
          !b.name.toLowerCase().includes('credit control')
        );
        setAvailableBundles(filteredBundles);
      }
    } catch (err) {
      console.error('Failed to fetch bundles:', err);
    } finally {
      setLoadingBundles(false);
    }
  }, [API_BASE_URL]);

  // Fetch bundles when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchBundles();
      setSelectedBundles([]);
    }
  }, [isOpen, fetchBundles]);

  // Toggle bundle selection
  const toggleBundle = (bundleId) => {
    setSelectedBundles(prev => 
      prev.includes(bundleId) 
        ? prev.filter(id => id !== bundleId)
        : [...prev, bundleId]
    );
  };

  // Get selected bundles data
  const getSelectedBundlesData = useCallback(() => {
    return selectedBundles.map(id => {
      const bundle = availableBundles.find(b => b.id === id);
      return bundle ? { id: bundle.id, name: bundle.name, price: parseFloat(bundle.price), link: bundle.link } : null;
    }).filter(Boolean);
  }, [selectedBundles, availableBundles]);

  // Calculate total amount
  const calculateTotalAmount = useCallback(() => {
    const bundleTotal = selectedBundles.reduce((total, bundleId) => {
      const bundle = availableBundles.find(b => b.id === bundleId);
      return total + (bundle ? parseFloat(bundle.price) : 0);
    }, 0);
    return 1500 + bundleTotal;
  }, [selectedBundles, availableBundles]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleExpectationToggle = (value) => {
    if (value === 'all') {
      setFormData(prev => ({
        ...prev,
        expectations: prev.expectations.includes('all') ? [] : ['all']
      }));
    } else {
      setFormData(prev => {
        const newExpectations = prev.expectations.includes(value)
          ? prev.expectations.filter(exp => exp !== value && exp !== 'all')
          : [...prev.expectations.filter(exp => exp !== 'all'), value];
        return {
          ...prev,
          expectations: newExpectations
        };
      });
    }
    if (errors.expectations) {
      setErrors(prev => ({
        ...prev,
        expectations: ''
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        // Validate Name & Designation
        if (!formData.nameDesignation.trim()) {
          newErrors.nameDesignation = 'Name & Designation is required';
        } else if (formData.nameDesignation.trim().length < 3) {
          newErrors.nameDesignation = 'Please enter at least 3 characters';
        }
        
        // Validate Company & Place
        if (!formData.companyPlace.trim()) {
          newErrors.companyPlace = 'Company Name & Place is required';
        } else if (formData.companyPlace.trim().length < 3) {
          newErrors.companyPlace = 'Please enter at least 3 characters';
        }
        
        // Validate Mobile Number
        if (!formData.mobileNumber.trim()) {
          newErrors.mobileNumber = 'Mobile number is required';
        } else if (!/^[6-9]\d{9}$/.test(formData.mobileNumber.replace(/\s/g, ''))) {
          newErrors.mobileNumber = 'Please enter a valid 10-digit Indian mobile number';
        }
        
        // Validate Alternate Mobile (optional but must be valid if provided)
        if (formData.alternateMobile.trim() && !/^[6-9]\d{9}$/.test(formData.alternateMobile.replace(/\s/g, ''))) {
          newErrors.alternateMobile = 'Please enter a valid 10-digit Indian mobile number';
        }
        break;
        
      case 2:
        if (formData.expectations.length === 0) {
          newErrors.expectations = 'Please select at least one expectation';
        }
        break;
        
      case 3:
        // Validate Turnover
        if (!formData.turnover.trim()) {
          newErrors.turnover = 'Business turnover is required';
        }
        
        // Validate Amount Stuck
        if (!formData.amountStuck) {
          newErrors.amountStuck = 'Please select an option';
        }
        break;
        
      case 4:
        if (!formData.painPoint.trim()) {
          newErrors.painPoint = 'Please describe your biggest pain point';
        } else if (formData.painPoint.trim().length < 20) {
          newErrors.painPoint = 'Please provide at least 20 characters';
        }
        break;
        
      case 5:
        // Validate Investment
        if (!formData.investment) {
          newErrors.investment = 'Please select an investment option';
        }
        
        // Validate Call Timing
        if (!formData.callTiming) {
          newErrors.callTiming = 'Please select your preferred timing';
        }
        break;
        
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(prev => prev + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const bundlesData = getSelectedBundlesData();
      const totalAmount = calculateTotalAmount();
      
      // Submit form data to backend
      const submitResponse = await fetch(`${API_BASE_URL}/requests/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          expectations: formData.expectations.join(', '),
          bundles: bundlesData,
          totalAmount: totalAmount,
          submittedAt: new Date().toISOString()
        })
      });

      if (!submitResponse.ok) {
        throw new Error('Failed to submit request');
      }

      // Show success briefly
      setIsSubmitted(true);

      // Load Razorpay and initiate payment
      await loadRazorpayScript();
      
      // Create payment record in backend
      let paymentId = null;
      try {
        const createResponse = await fetch(`${API_BASE_URL}/payments/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.nameDesignation,
            email: '', // Email not collected in request form
            phone: formData.mobileNumber,
            amount: totalAmount,
            productName: "8-Week Credit Control & Payment Recovery Coaching",
            bundles: bundlesData
          })
        });
        const createData = await createResponse.json();
        if (createData.success) {
          paymentId = createData.paymentId;
        }
      } catch (err) {
        console.error('Failed to create payment record:', err);
      }

      // Initiate Razorpay payment
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_live_xxxxxxxxxxxxxxx',
        amount: totalAmount * 100,
        currency: 'INR',
        name: 'Rushneek Singh',
        description: "8-Week Credit Control & Payment Recovery Coaching" + (bundlesData.length > 0 ? ` + ${bundlesData.length} Add-ons` : ''),
        image: 'https://lp1.rushneeksingh.com/static/media/logo.png',
        prefill: {
          name: formData.nameDesignation,
          email: '',
          contact: formData.mobileNumber
        },
        notes: {
          customer_name: formData.nameDesignation,
          customer_phone: formData.mobileNumber,
          product: "8-Week Credit Control & Payment Recovery Coaching",
          paymentId: paymentId,
          bundles: JSON.stringify(bundlesData)
        },
        theme: {
          color: '#F59E0B',
          backdrop_color: 'rgba(15, 23, 42, 0.9)'
        },
        modal: {
          ondismiss: async function() {
            try {
              await fetch(`${API_BASE_URL}/payments/pending`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  paymentId,
                  name: formData.nameDesignation,
                  email: '',
                  phone: formData.mobileNumber,
                  amount: totalAmount,
                  productName: "8-Week Credit Control & Payment Recovery Coaching"
                })
              });
            } catch (err) {
              console.error('Failed to record pending payment:', err);
            }
            setIsSubmitting(false);
            handleClose();
          }
        },
        handler: async function(response) {
          try {
            await fetch(`${API_BASE_URL}/payments/success`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                paymentId,
                name: formData.nameDesignation,
                email: '',
                phone: formData.mobileNumber,
                amount: totalAmount,
                productName: "8-Week Credit Control & Payment Recovery Coaching",
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                payment_method: 'razorpay',
                bundles: bundlesData
              })
            });
          } catch (err) {
            console.error('Failed to record successful payment:', err);
          }
          
          alert(`Payment Successful!\n\nPayment ID: ${response.razorpay_payment_id}\n\nThank you, ${formData.nameDesignation}! Our team will contact you shortly to schedule your consultation.`);
          setIsSubmitting(false);
          handleClose();
        }
      };

      const razorpay = new window.Razorpay(options);
      
      razorpay.on('payment.failed', async function(response) {
        try {
          await fetch(`${API_BASE_URL}/payments/failed`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              paymentId,
              name: formData.nameDesignation,
              email: '',
              phone: formData.mobileNumber,
              amount: totalAmount,
              productName: "8-Week Credit Control & Payment Recovery Coaching",
              error_code: response.error.code,
              error_description: response.error.description
            })
          });
        } catch (err) {
          console.error('Failed to record failed payment:', err);
        }
        
        alert(`Payment Failed!\n\nReason: ${response.error.description}\n\nPlease try again or contact support.`);
        setIsSubmitting(false);
        handleClose();
      });
      
      razorpay.open();

    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again or contact us directly at 8920633120.');
      setIsSubmitting(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleClose = () => {
    setCurrentStep(1);
    setFormData({
      nameDesignation: '',
      companyPlace: '',
      mobileNumber: '',
      alternateMobile: '',
      expectations: [],
      turnover: '',
      amountStuck: '',
      painPoint: '',
      investment: '',
      callTiming: ''
    });
    setErrors({});
    setIsSubmitted(false);
    setSelectedBundles([]);
    onClose();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <FaUser className="inline-block text-4xl text-amber-400 mb-3" />
              <h3 className="text-2xl font-black text-white">Personal & Company Information</h3>
              <p className="text-slate-400 text-sm mt-2">Tell us about yourself and your company</p>
            </div>
            
            {/* Name & Designation */}
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                Name & Designation *
              </label>
              <div className="relative">
                <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 z-10" />
                <input
                  type="text"
                  name="nameDesignation"
                  value={formData.nameDesignation}
                  onChange={handleInputChange}
                  placeholder="e.g., John Doe - CEO"
                  className={`w-full bg-slate-700/50 border ${errors.nameDesignation ? 'border-red-500' : 'border-slate-600'} rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all duration-200`}
                />
              </div>
              {errors.nameDesignation && (
                <p className="mt-1 text-sm text-red-400">{errors.nameDesignation}</p>
              )}
            </div>

            {/* Company & Place */}
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                Company Name & Place *
              </label>
              <div className="relative">
                <FaBuilding className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 z-10" />
                <input
                  type="text"
                  name="companyPlace"
                  value={formData.companyPlace}
                  onChange={handleInputChange}
                  placeholder="e.g., ABC Pvt Ltd, Delhi"
                  className={`w-full bg-slate-700/50 border ${errors.companyPlace ? 'border-red-500' : 'border-slate-600'} rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all duration-200`}
                />
              </div>
              {errors.companyPlace && (
                <p className="mt-1 text-sm text-red-400">{errors.companyPlace}</p>
              )}
            </div>

            {/* Mobile Number */}
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                Mobile Number *
              </label>
              <div className="relative">
                <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 z-10" />
                <input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  placeholder="10-digit mobile number"
                  maxLength="10"
                  className={`w-full bg-slate-700/50 border ${errors.mobileNumber ? 'border-red-500' : 'border-slate-600'} rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all duration-200`}
                />
              </div>
              {errors.mobileNumber && (
                <p className="mt-1 text-sm text-red-400">{errors.mobileNumber}</p>
              )}
            </div>

            {/* Alternate Mobile */}
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                Alternate Mobile Number <span className="text-slate-500 text-xs">(Optional)</span>
              </label>
              <div className="relative">
                <FaPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 z-10" />
                <input
                  type="tel"
                  name="alternateMobile"
                  value={formData.alternateMobile}
                  onChange={handleInputChange}
                  placeholder="10-digit mobile number (optional)"
                  maxLength="10"
                  className={`w-full bg-slate-700/50 border ${errors.alternateMobile ? 'border-red-500' : 'border-slate-600'} rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all duration-200`}
                />
              </div>
              {errors.alternateMobile && (
                <p className="mt-1 text-sm text-red-400">{errors.alternateMobile}</p>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <FaBullseye className="inline-block text-4xl text-amber-400 mb-3" />
              <h3 className="text-2xl font-black text-white">Your Expectations</h3>
              <p className="text-slate-400 text-sm mt-2">What are you looking for? (Select all that apply)</p>
            </div>
            <div className="space-y-3">
              {expectationOptions.map(option => {
                const Icon = option.icon;
                const isSelected = formData.expectations.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                      isSelected
                        ? 'bg-amber-500/20 border-2 border-amber-500 shadow-lg shadow-amber-500/20'
                        : 'bg-slate-700/30 border-2 border-slate-600 hover:border-slate-500'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                      isSelected
                        ? 'bg-amber-500 text-slate-900'
                        : 'bg-slate-600 border-2 border-slate-500'
                    }`}>
                      {isSelected && <FaCheckCircle className="text-sm" />}
                    </div>
                    <Icon className={`text-2xl ${isSelected ? 'text-amber-400' : 'text-slate-500'}`} />
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleExpectationToggle(option.value)}
                      className="hidden"
                    />
                    <span className={`flex-1 font-semibold ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                      {option.label}
                    </span>
                  </label>
                );
              })}
            </div>
            {errors.expectations && (
              <p className="mt-1 text-sm text-red-400 text-center">{errors.expectations}</p>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <FaChartLine className="inline-block text-4xl text-amber-400 mb-3" />
              <h3 className="text-2xl font-black text-white">Business Details</h3>
              <p className="text-slate-400 text-sm mt-2">Tell us about your business financials</p>
            </div>
            
            {/* Business Turnover */}
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                Last Year Business Turnover (in Numbers) *
              </label>
              <div className="relative">
                <FaRupeeSign className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 z-10" />
                <input
                  type="text"
                  name="turnover"
                  value={formData.turnover}
                  onChange={handleInputChange}
                  placeholder="e.g., 50 Lakhs or 2 Crores"
                  className={`w-full bg-slate-700/50 border ${errors.turnover ? 'border-red-500' : 'border-slate-600'} rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all duration-200`}
                />
              </div>
              {errors.turnover && (
                <p className="mt-1 text-sm text-red-400">{errors.turnover}</p>
              )}
            </div>

            {/* Amount Stuck */}
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                Amount Stuck in Market? *
              </label>
              <div className="space-y-2">
                {amountStuckOptions.map(option => {
                  const isSelected = formData.amountStuck === option;
                  return (
                    <label
                      key={option}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? 'bg-amber-500/20 border-2 border-amber-500 shadow-lg shadow-amber-500/20'
                          : 'bg-slate-700/30 border-2 border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                        isSelected
                          ? 'bg-amber-500 border-4 border-slate-900'
                          : 'bg-slate-600 border-2 border-slate-500'
                      }`}>
                      </div>
                      <input
                        type="radio"
                        name="amountStuck"
                        value={option}
                        checked={isSelected}
                        onChange={handleInputChange}
                        className="hidden"
                      />
                      <span className={`flex-1 font-semibold text-sm ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                        {option}
                      </span>
                    </label>
                  );
                })}
              </div>
              {errors.amountStuck && (
                <p className="mt-1 text-sm text-red-400 text-center">{errors.amountStuck}</p>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <FaExclamationCircle className="inline-block text-4xl text-amber-400 mb-3" />
              <h3 className="text-2xl font-black text-white">Your Biggest Challenge</h3>
              <p className="text-slate-400 text-sm mt-2">What problem do you want to solve immediately?</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                What is the biggest pain point or problem you are currently facing in your business that you would like to SOLVE IMMEDIATELY? *
              </label>
              <textarea
                name="painPoint"
                value={formData.painPoint}
                onChange={handleInputChange}
                placeholder="Describe your biggest challenge in detail..."
                rows="6"
                className={`w-full bg-slate-700/50 border ${errors.painPoint ? 'border-red-500' : 'border-slate-600'} rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 transition-all duration-200 resize-none`}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.painPoint && (
                  <p className="text-sm text-red-400">{errors.painPoint}</p>
                )}
                <p className="text-xs text-slate-500 ml-auto">
                  {formData.painPoint.length} / 20 minimum characters
                </p>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <FaRupeeSign className="inline-block text-4xl text-amber-400 mb-3" />
              <h3 className="text-2xl font-black text-white">Investment & Scheduling</h3>
              <p className="text-slate-400 text-sm mt-2">Final details to complete your request</p>
            </div>
            
            {/* Investment Amount */}
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                How much are you willing to invest? *
              </label>
              <div className="space-y-3">
                {investmentOptions.map(option => {
                  const isSelected = formData.investment === option.value;
                  return (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-2 border-amber-500 shadow-lg shadow-amber-500/20'
                          : 'bg-slate-700/30 border-2 border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                        isSelected
                          ? 'bg-amber-500 border-4 border-slate-900'
                          : 'bg-slate-600 border-2 border-slate-500'
                      }`}>
                      </div>
                      <input
                        type="radio"
                        name="investment"
                        value={option.value}
                        checked={isSelected}
                        onChange={handleInputChange}
                        className="hidden"
                      />
                      <span className={`flex-1 text-lg font-bold ${isSelected ? 'text-amber-400' : 'text-slate-300'}`}>
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
              {errors.investment && (
                <p className="mt-1 text-sm text-red-400 text-center">{errors.investment}</p>
              )}
            </div>

            {/* Call Timing */}
            <div>
              <label className="block text-sm font-semibold text-slate-300 mb-2">
                When would you like to book a call with Rushneek Sir? *
              </label>
              <div className="space-y-3">
                {callTimingOptions.map(option => {
                  const Icon = option.icon;
                  const isSelected = formData.callTiming === option.value;
                  return (
                    <label
                      key={option.value}
                      className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? 'bg-amber-500/20 border-2 border-amber-500 shadow-lg shadow-amber-500/20'
                          : 'bg-slate-700/30 border-2 border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                        isSelected
                          ? 'bg-amber-500 border-4 border-slate-900'
                          : 'bg-slate-600 border-2 border-slate-500'
                      }`}>
                      </div>
                      <Icon className={`text-xl ${isSelected ? 'text-amber-400' : 'text-slate-500'}`} />
                      <input
                        type="radio"
                        name="callTiming"
                        value={option.value}
                        checked={isSelected}
                        onChange={handleInputChange}
                        className="hidden"
                      />
                      <span className={`flex-1 font-semibold ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                        {option.label}
                      </span>
                    </label>
                  );
                })}
              </div>
              {errors.callTiming && (
                <p className="mt-1 text-sm text-red-400 text-center">{errors.callTiming}</p>
              )}
            </div>

            {/* Add-on Bundles Section */}
            {availableBundles.length > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-700">
                <div className="flex items-center gap-2 mb-3">
                  <FaPlus className="text-amber-400" />
                  <span className="text-sm font-bold text-white">Add More Courses (Optional)</span>
                </div>
                <div className="space-y-2">
                  {availableBundles.map(bundle => (
                    <label 
                      key={bundle.id}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                        selectedBundles.includes(bundle.id)
                          ? 'bg-amber-500/20 border-2 border-amber-500'
                          : 'bg-slate-700/30 border-2 border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-all ${
                        selectedBundles.includes(bundle.id)
                          ? 'bg-amber-500 text-slate-900'
                          : 'bg-slate-600 border border-slate-500'
                      }`}>
                        {selectedBundles.includes(bundle.id) && <FaCheck className="text-xs" />}
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedBundles.includes(bundle.id)}
                        onChange={() => toggleBundle(bundle.id)}
                        className="hidden"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-white leading-tight">{bundle.name}</p>
                      </div>
                      <span className="text-amber-400 font-bold text-sm whitespace-nowrap">
                        +₹{parseFloat(bundle.price).toLocaleString('en-IN')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {loadingBundles && (
              <div className="text-center py-2">
                <span className="text-slate-400 text-sm">Loading add-ons...</span>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="space-y-4">
            <p className="text-white text-center">Loading step {currentStep}...</p>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  // Success Screen
  if (isSubmitted) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm"></div>
        <div className="relative w-full max-w-md bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-3xl shadow-2xl shadow-black/50 border border-amber-500/20 p-8 text-center animate-fadeIn">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/30">
            <FaCheckCircle className="text-4xl text-white" />
          </div>
          <h2 className="text-3xl font-black text-white mb-3">Request Submitted!</h2>
          <p className="text-slate-300 mb-6">
            Opening secure payment gateway...
          </p>
          <div className="bg-slate-800/50 rounded-xl p-4 mb-6 border border-amber-500/20">
            <p className="text-amber-400 font-bold mb-2">📞 Consultation Fee: ₹{calculateTotalAmount().toLocaleString('en-IN')}</p>
            <p className="text-slate-400 text-sm">
              Secure payment via Razorpay
            </p>
          </div>
          <div className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5 text-amber-400" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-slate-400 text-sm">
              Please wait...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm"
        onClick={handleClose}
      ></div>

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-3xl shadow-2xl shadow-black/50 border border-amber-500/20">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500"></div>
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-orange-500/10 rounded-full blur-3xl"></div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-slate-700/50 hover:bg-slate-600/50 text-slate-400 hover:text-white transition-all duration-200 z-10"
        >
          <FaTimes className="text-lg" />
        </button>

        {/* Content */}
        <div className="relative p-6 sm:p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-3xl font-black text-white mb-2">
              Request Form
            </h2>
            <p className="text-amber-400 font-bold text-sm">
              Premium Consultancy
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-400">Step {currentStep} of {totalSteps}</span>
              <span className="text-xs font-semibold text-amber-400">{Math.round((currentStep / totalSteps) * 100)}%</span>
            </div>
            <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Step Content */}
          <div key={currentStep} className="mb-6 min-h-[300px]">
            {renderStepContent()}
          </div>

          {/* Price Summary - Show on last step */}
          {currentStep === totalSteps && (
            <div className="mb-6 pt-4 border-t border-slate-700 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Consultation Fee</span>
                <span className="text-white">₹1,500</span>
              </div>
              {selectedBundles.length > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Add-ons ({selectedBundles.length})</span>
                  <span className="text-amber-400">+₹{(calculateTotalAmount() - 1500).toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-slate-600">
                <span className="text-white font-bold">Total Amount</span>
                <div className="flex items-center gap-1">
                  <FaRupeeSign className="text-amber-400 text-lg" />
                  <span className="text-2xl font-black text-amber-400">{calculateTotalAmount().toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3">
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                className="flex items-center gap-2 px-6 py-3 bg-slate-700/50 hover:bg-slate-600/50 text-white rounded-xl font-bold transition-all duration-200 border border-slate-600"
              >
                <FaArrowLeft />
                Previous
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500 text-slate-900 py-3 rounded-xl text-lg font-extrabold shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </>
              ) : currentStep === totalSteps ? (
                <>
                  Submit & Proceed to Payment
                  <FaArrowRight />
                </>
              ) : (
                <>
                  Next
                  <FaArrowRight />
                </>
              )}
            </button>
          </div>

          {/* Payment Notice */}
          {currentStep === totalSteps && (
            <div className="mt-6 bg-slate-800/50 rounded-xl p-4 border border-amber-500/20">
              <p className="text-amber-400 font-bold text-sm mb-2">📞 Consultation Call Fee: ₹1,500</p>
              <p className="text-slate-400 text-xs leading-relaxed">
                This amount will be refunded on the spot during the meeting. We charge this fee to avoid no-shows and to respect both your and our time.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* CSS Animation */}
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out forwards;
            opacity: 1 !important;
          }
        `}
      </style>
    </div>
  );
}

export default RequestFormModal;
